import { UcoastEl } from '@/scripts/core/UcoastEl'
import { qsRequired } from '@/scripts/core/global'

// interface BundleVariant {
//   sku: string
//   title: string
//   default: boolean
//   enabled: boolean
//   variantId: string
//   variantGid: string
//   fakeSoldOut: boolean
// }

// interface BundleProduct {
//   order: number
//   title: string
//   category: string
//   imageSrc: string
//   quantity: number
//   variants: BundleVariant[]
//   productId: string
//   productGid: string
//   productHandle: string
// }

// interface BundleCategory {
//   id: string
//   name: string
//   order: number
//   products: BundleProduct[]
// }

// interface BundleData {
//   parent: {
//     sku: string
//     title: string
//     category: string
//     productId: string
//     variantId: string
//     productGid: string
//     variantGid: string
//     productHandle: string
//   }
//   content: BundleCategory[]
//   recipes?: any[]
//   discount?: any
// }

interface CartItem {
  id: string
  quantity: number
  properties: {
    _bundleId: string
    _size: number
    _uniqueId: string
  }
}

interface DiscountTier {
  minQuantity: number
  discount: number
}

interface BundleDiscount {
  title: string
  id: string
  discountType: 'allProducts' | 'tierDiscount'
  tiers?: DiscountTier[]
  discount?: string
  products: any[]
  startDate: string
  endDate?: string | null
}

interface BundleData {
  parent: any
  content: any[]
  recipes?: any[]
  discount?: BundleDiscount
}

export class BundleComponent extends UcoastEl {
  static htmlSelector = 'bundle-component'

  private form!: HTMLFormElement
  private ctaContainer!: HTMLElement
  private progressBar?: HTMLElement | null
  private currentValueDisplay?: HTMLElement | null
  private bundleId: string
  private bundleData: BundleData

  constructor() {
    super()
    
    this.bundleData = JSON.parse(this.getAttribute('data-bundle') || '{}')
    this.bundleId = this.getAttribute('data-bundle-id') || ''
    // console.log('BundleComponent initialized')
  }

  override connectedCallback() {
    super.connectedCallback()
    
    // Wait for the DOM to be ready
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => this.init())
    } else {
      // Use setTimeout to ensure all content is rendered
      setTimeout(() => this.init(), 100)
    }
  }

  private init(): void {
    // Initialize required elements
    this.form = qsRequired('form', this)
    this.ctaContainer = qsRequired('#cta-container', this)
    
    // console.log('BundleComponent initialized - setting up...')
    
    this.debugInitialState()
    this.setupEventListeners()
    this.setupProgressBar()
    
    // console.log('Calling initial updateTotal()...')
    this.updateTotal()
    // console.log('Calling initial updateProgressDisplay()...')
    this.updateProgressDisplay()

    this.hideStickyCTA()
    // console.log('BundleComponent setup complete')
  }

  private debugInitialState(): void {
    // console.log('BundleComponent: Elements initialized')
  }

  private setupEventListeners(): void {
    // Checkbox change events
    this.form.addEventListener('change', (e) => {
      if ((e.target as HTMLInputElement).type === 'checkbox') {
        // console.log('Checkbox changed, updating total and progress...')
        this.updateTotal()
        this.updateProgressDisplay()
      }
    })

    // Check/Uncheck all buttons using event delegation
    this.addEventListener('click', (e) => {
      const target = e.target as HTMLElement
      
      // Only handle category-level uncheck/check buttons, not individual product edit buttons
      if (target.classList.contains('bundle-check-all') && 
          target.closest('.bundle-content-category-header')) {
        e.preventDefault()
        const container = target.closest('.bundle-content-category-container')
        const checkboxes = container?.querySelectorAll('input[type="checkbox"]') as NodeListOf<HTMLInputElement>
        const uncheckAllBtn = container?.querySelector('.bundle-uncheck-all') as HTMLButtonElement
        
        checkboxes.forEach(checkbox => checkbox.checked = true)
        target.classList.add('hidden')
        uncheckAllBtn?.classList.remove('hidden')
        // console.log('Check All button clicked, updating total and progress...')
        this.updateTotal()
        this.updateProgressDisplay()
      } else if (target.classList.contains('bundle-uncheck-all') && 
                target.closest('.bundle-content-category-header')) {
        e.preventDefault()
        const container = target.closest('.bundle-content-category-container')
        const checkboxes = container?.querySelectorAll('input[type="checkbox"]') as NodeListOf<HTMLInputElement>
        const checkAllBtn = container?.querySelector('.bundle-check-all') as HTMLButtonElement
        
        checkboxes.forEach(checkbox => checkbox.checked = false)
        target.classList.add('hidden')
        checkAllBtn?.classList.remove('hidden')
        // console.log('Uncheck All button clicked, updating total and progress...')
        this.updateTotal()
        this.updateProgressDisplay()
      }
    })

    // Tooltip functionality
    this.setupTooltips()
  }

    private setupTooltips(): void {
    // Use event delegation for better performance
    this.addEventListener('click', (e) => {
      const target = e.target as HTMLElement
      
      // Handle close buttons
      if (target.classList.contains('close_btn')) {
        const tooltip = target.parentElement
        tooltip?.classList.remove('active')
        return
      }
      
      // Handle tooltip triggers
      if (target.closest('.product-tooltip__trigger')) {
        const trigger = target.closest('.product-tooltip__trigger') as HTMLElement
        const parent = trigger.parentElement
        const tooltip = parent?.querySelector('.product-tooltip')
        
        // Close all tooltips first
        this.querySelectorAll('.product-tooltip.active').forEach(activeTooltip => {
          if (activeTooltip !== tooltip) {
            activeTooltip.classList.remove('active')
          }
        })

        // Toggle current tooltip
        if (tooltip) {
          tooltip.classList.add('active')
          this.syncTooltipInputs(tooltip, trigger)
          
          // Close on outside click (support both click and touch events)
          const handleOutsideClick = (event: Event) => {
            const target = event.target as Node
            if (!tooltip.contains(target) && !trigger.contains(target)) {
              tooltip.classList.remove('active')
              document.removeEventListener('click', handleOutsideClick)
              document.removeEventListener('touchstart', handleOutsideClick)
            }
          }
          
          setTimeout(() => {
            document.addEventListener('click', handleOutsideClick)
            document.addEventListener('touchstart', handleOutsideClick)
          }, 0)
        }
      }
    })

    // Event delegation for inputs
    this.addEventListener('change', (e) => {
      const target = e.target as HTMLElement
      
      if (target.classList.contains('quantity-input')) {
        this.handleQuantityChange(target as HTMLInputElement)
      } else if (target.classList.contains('tooltip-select')) {
        this.handleVariantChange(target as HTMLSelectElement)
      }
    })

    // Additional event listener for mobile compatibility with select elements
    this.addEventListener('input', (e) => {
      const target = e.target as HTMLElement
      
      if (target.classList.contains('tooltip-select')) {
        this.handleVariantChange(target as HTMLSelectElement)
      }
    })

    // Prevent ProductTooltip.ts from interfering with our select events
    this.addEventListener('click', (e) => {
      const target = e.target as HTMLElement
      if (target.classList.contains('tooltip-select') || target.closest('.tooltip-selectors')) {
        e.stopPropagation()
      }
    })
  }

  private handleQuantityChange(input: HTMLInputElement): void {
    const uniqueId = input.id.split('quantity-')[1]
    const value = Math.max(parseInt(input.value) || 1, 1)
    input.value = value.toString()

    const label = this.querySelector(`#label-${uniqueId}`)
    if (label) {
      // Update label quantity only
      const quantitySpan = label.querySelector('.label-quantity')
      if (quantitySpan) {
        quantitySpan.textContent = value.toString()
      }
      
      // Update prices and checkbox in one go
      this.updateProductData(uniqueId, value)
    }
  }

  private handleVariantChange(select: HTMLSelectElement): void {
    const uniqueId = select.id.split('variant-')[1]
    const selectedOption = select.options[select.selectedIndex]
    const variantTitle = selectedOption.text
    const selectedVariantId = select.value

    // Prevent duplicate processing by checking if this is already the current value
    const currentVariantId = select.dataset.lastProcessedValue
    if (currentVariantId === selectedVariantId) {
      return
    }
    select.dataset.lastProcessedValue = selectedVariantId

    const label = this.querySelector(`#label-${uniqueId}`)
    if (label) {
      // Update variant name only
      const variantSpan = label.querySelector('.label-variant-name')
      if (variantSpan) {
        variantSpan.textContent = variantTitle
      }
      
      // Update prices and checkbox in one go
      this.updateProductData(uniqueId, undefined, selectedVariantId)
    }
  }

  // Consolidated method for updating product data
  private updateProductData(uniqueId: string, quantity?: number, variantId?: string): void {
    // Get current values if not provided
    const tooltip = this.querySelector(`[data-tooltip-id*="${uniqueId}"] .product-tooltip`)
    if (!tooltip) return
    
    const variantSelect = tooltip.querySelector(`#variant-${uniqueId}`) as HTMLSelectElement
    const quantityInput = tooltip.querySelector(`#quantity-${uniqueId}`) as HTMLInputElement
    
    const currentQuantity = quantity || parseInt(quantityInput?.value || '1')
    const currentVariantId = variantId || variantSelect?.value

    // Update tooltip price
    this.updateTooltipPrice(uniqueId, currentQuantity, currentVariantId)
    
    // Update checkbox attributes
    this.updateCheckboxAttributes(uniqueId, currentQuantity, currentVariantId)
  }





  private updateTooltipPrice(uniqueId: string, quantity: number = 1, variantId?: string): void {
    const tooltip = this.querySelector(`[data-tooltip-id*="${uniqueId}"] .product-tooltip`)
    if (!tooltip) return

    const variantSelect = tooltip.querySelector(`#variant-${uniqueId}`) as HTMLSelectElement
    const actualVariantId = variantId || variantSelect?.value
    
    if (actualVariantId) {
      const hiddenPriceElement = tooltip.querySelector(`#variant-price-hidden-${actualVariantId}`)
      const priceElement = tooltip.querySelector('#tooltip-price')

      if (hiddenPriceElement && priceElement) {
        const basePrice = hiddenPriceElement.textContent?.trim() || ''
        const priceMatch = basePrice.match(/[\d.]+/)
        if (priceMatch) {
          const priceValue = parseFloat(priceMatch[0])
          const newPrice = priceValue * quantity
          priceElement.textContent = `$${newPrice.toFixed(2)}`
        }
      }
    }
  }

  private updateCheckboxAttributes(uniqueId: string, quantity: number = 1, variantId?: string): void {
    // Find the checkbox associated with this unique product
    let checkbox = this.form.querySelector(`input[type="checkbox"][data-unique-id="${uniqueId}"]`) as HTMLInputElement
    
    if (!checkbox) {
      // Fallback: find by tooltip container - use more efficient selector
      const tooltipTrigger = this.querySelector(`[data-tooltip-id*="${uniqueId}"]`)
      if (tooltipTrigger) {
        const tooltipContainer = tooltipTrigger.closest('.recipe-content__ingredient-item')
        checkbox = tooltipContainer?.querySelector('input[type="checkbox"]') as HTMLInputElement
      }
    }
    
    if (checkbox) {
      this.updateCheckboxData(checkbox, quantity, variantId)
    }
  }

  private updateCheckboxData(checkbox: HTMLInputElement, quantity: number, variantId?: string): void {
    // console.log('Updating checkbox data:', {
    //   currentValue: checkbox.value,
    //   newQuantity: quantity,
    //   newVariantId: variantId
    // })
    
    // Update quantity
    checkbox.setAttribute('data-quantity', quantity.toString())
    
    // Update variant ID and price if provided
    if (variantId) {
      checkbox.value = variantId
      
      // Find the price for this variant
      const hiddenPriceElement = this.querySelector(`#variant-price-hidden-${variantId}`)
      if (hiddenPriceElement) {
        const basePrice = hiddenPriceElement.textContent?.trim() || ''
        const priceMatch = basePrice.match(/[\d.]+/)
        if (priceMatch) {
          const priceValue = Math.round(parseFloat(priceMatch[0]) * 100) // Convert to cents
          checkbox.setAttribute('data-price', priceValue.toString())
          // console.log('Updated price for variant:', variantId, 'to:', priceValue, 'cents')
        }
      }
    }
    
    // console.log('Checkbox updated, recalculating total...')
    // Recalculate total after updating checkbox data
    this.updateTotal()
    this.updateProgressDisplay()
  }

  private updateTotal(): void {
    const checkboxes = this.form.querySelectorAll('input[type="checkbox"]:checked') as NodeListOf<HTMLInputElement>
    let total = 0

    // console.log('=== UPDATE TOTAL DEBUG ===')
    // console.log('Found checked checkboxes:', checkboxes.length)

    if (checkboxes.length === 0) {
      console.warn('No checkboxes are checked!')
      // Check all checkboxes to see their state
      // const allCheckboxes = this.form.querySelectorAll('input[type="checkbox"]') as NodeListOf<HTMLInputElement>
      // console.log('All checkboxes in form:', allCheckboxes.length)
      // allCheckboxes.forEach((cb, index) => {
      //   console.log(`Checkbox ${index}:`, {
      //     id: cb.id,
      //     checked: cb.checked,
      //     value: cb.value,
      //     dataPrice: cb.getAttribute('data-price'),
      //     dataQuantity: cb.getAttribute('data-quantity'),
      //     dataProductId: cb.getAttribute('data-product-id')
      //   })
      // })
    }

    checkboxes.forEach((checkbox) => {
      const priceAttr = checkbox.getAttribute('data-price')
      const quantityAttr = checkbox.getAttribute('data-quantity')
      const price = parseFloat(priceAttr || '0')
      const quantity = parseInt(quantityAttr || '1')
      const lineTotal = price * quantity
      total += lineTotal
      
      // console.log(`Checked Checkbox ${index}:`, {
      //   id: checkbox.id,
      //   value: checkbox.value,
      //   priceAttr: priceAttr,
      //   quantityAttr: quantityAttr,
      //   parsedPrice: price,
      //   parsedQuantity: quantity,
      //   lineTotal: lineTotal,
      //   dataProductId: checkbox.getAttribute('data-product-id')
      // })

      if (price === 0) {
        console.warn('Checkbox has zero price! This might be the problem.')
      }
    })

    const finalTotal = total / 100
    const discount = this.calculateDiscount(checkboxes.length)
    
    // console.log('Final calculation:', {
    //   totalCents: total,
    //   finalTotal: finalTotal,
    //   discount: discount,
    //   selectedCount: checkboxes.length,
    //   discountType: this.bundleData?.discount?.discountType || 'no-discount',
    //   hasDiscountData: !!this.bundleData?.discount
    // })
    
    this.updateCTAButtons(finalTotal, discount)
    this.updateRecipesFilter()
  }

  private calculateDiscount(selectedCount: number): number {
    // console.log('=== CALCULATE DISCOUNT DEBUG ===')
    // console.log('Selected count:', selectedCount)
    // console.log('Bundle data:', this.bundleData)
    // console.log('Discount config:', this.bundleData?.discount)
    
    // Check if we have discount data from bundle JSON
    if (this.bundleData?.discount) {
      const discountConfig = this.bundleData.discount
      // console.log('Using discount type:', discountConfig.discountType)
      
      if (discountConfig.discountType === 'allProducts') {
        // Check if ALL available checkboxes are selected
        const allCheckboxes = this.form.querySelectorAll('input[type="checkbox"]:not([disabled])') as NodeListOf<HTMLInputElement>
        const checkedCheckboxes = this.form.querySelectorAll('input[type="checkbox"]:not([disabled]):checked') as NodeListOf<HTMLInputElement>
        
        // console.log('AllProducts check:', {
        //   totalCheckboxes: allCheckboxes.length,
        //   checkedCheckboxes: checkedCheckboxes.length,
        //   allSelected: allCheckboxes.length === checkedCheckboxes.length,
        //   discountValue: discountConfig.discount
        // })
        
        if (allCheckboxes.length === checkedCheckboxes.length && discountConfig.discount) {
          // Apply discount percentage (convert from percentage to multiplier)
          const discountPercent = parseFloat(discountConfig.discount)
          const multiplier = (100 - discountPercent) / 100
          // console.log('Applying allProducts discount:', discountPercent + '% = multiplier', multiplier)
          return multiplier
        }
        // console.log('No allProducts discount applied - not all items selected')
        return 1 // No discount if not all selected
        
      } else if (discountConfig.discountType === 'tierDiscount' && discountConfig.tiers) {
        // Sort tiers by minQuantity descending to check highest tiers first
        const sortedTiers = [...discountConfig.tiers].sort((a, b) => b.minQuantity - a.minQuantity)
        // console.log('TierDiscount tiers:', sortedTiers)
        
        for (const tier of sortedTiers) {
          if (selectedCount >= tier.minQuantity) {
            // Convert discount percentage to multiplier
            const multiplier = (100 - tier.discount) / 100
            // console.log('Applying tier discount:', tier.discount + '% = multiplier', multiplier, 'for tier', tier.minQuantity)
            return multiplier
          }
        }
        // console.log('No tier discount applied - count below minimum')
        return 1 // No discount if no tier matches
      }
    }
    
    // No discount
    return 1;
  }

  private updateCTAButtons(total: number, discount: number): void {
    // console.log('Updating CTA buttons with total:', total, 'discount:', discount)
    
    const buttonHTML = this.generateButtonHTML(total, discount)
    // console.log('Generated button HTML:', buttonHTML)
    
    // Update main CTA
    const newCTA = this.ctaContainer.cloneNode(true) as HTMLElement
    newCTA.innerHTML = buttonHTML
    this.ctaContainer.parentNode?.replaceChild(newCTA, this.ctaContainer)
    this.ctaContainer = newCTA
    
    // console.log('Updated main CTA container')
    
    // Add event listener
    newCTA.addEventListener('click', (e) => this.onSubmit(e))

    // Update other CTAs
    const otherCTAs = document.querySelectorAll('#bundle-content-buy-button, #bundle-content-buy-button-2')
    // console.log('Found other CTAs to update:', otherCTAs.length)
    
    otherCTAs.forEach((cta) => {
      const newCTA = cta.cloneNode(true) as HTMLElement
      newCTA.innerHTML = buttonHTML
      cta.parentNode?.replaceChild(newCTA, cta)
      newCTA.addEventListener('click', (e) => this.onSubmit(e))
      // console.log(`Updated CTA ${index + 1}`)
    })
    
    // console.log('All CTA buttons updated successfully')
  }

  private generateButtonHTML(total: number, discount: number): string {
    const finalPrice = total * (discount || 1)
    
    // console.log('Generating button HTML:', {
    //   total: total,
    //   discount: discount,
    //   finalPrice: finalPrice,
    //   hasDiscount: discount > 0
    // })
    
    if (discount === 1) {
      const buttonHTML = `
        <button type="button" class="button">
          <span>$${total.toFixed(2).replace(/\.00$/, '')}</span>
          <span>&nbsp;–&nbsp;Add to Cart</span>
        </button>
      `
      // console.log ('Generated no-discount button HTML')
      return buttonHTML
    } else {
      const discountPercent = Math.round((1 - discount) * 100)
      const buttonHTML = `
        <button type="button" class="button">
          <span>
            <s>$${total.toFixed(2).replace(/\.00$/, '')}</s>
            $${finalPrice.toFixed(2).replace(/\.00$/, '')}
          </span>
          <span>&nbsp;–&nbsp;Add to Cart</span>
        </button>
        <div style='min-width: 80px;'>
          <span class="label-body recipe-content__cta__discount tt-uppercase">&nbsp;&nbsp;${discountPercent}% off</span>
        </div>
      `
      // console.log('Generated discount button HTML with', discountPercent, '% off')
      return buttonHTML
    }
  }

  private async onSubmit(e: Event): Promise<void> {
    e.preventDefault()
    
    const checkboxes = this.form.querySelectorAll('input[type="checkbox"]:checked') as NodeListOf<HTMLInputElement>
    
    if (checkboxes.length < 2) {
      alert('Please select at least two products to bundle')
      return
    }

    const allCheckboxes = this.form.querySelectorAll('input[type="checkbox"]')
    const time = new Date().getTime()
    
    const items: CartItem[] = Array.from(checkboxes).map(checkbox => ({
      id: checkbox.value,
      quantity: parseInt(checkbox.getAttribute('data-quantity') || '1'),
      properties: {
        _bundleId: this.bundleId,
        _size: allCheckboxes.length,
        _uniqueId: time.toString(),
        _version: 'v3'
      }
    }))

    try {
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ items })
      })

      if (response.ok) {
        // console.log('Bundle added to cart successfully')
        // Trigger cart update events here if needed
      } else {
        const error = await response.json()
        console.error('Error adding bundle to cart:', error)
      }
    } catch (error) {
      console.error('Error adding bundle to cart:', error)
    }
  }

  private setupProgressBar(): void {
    const progressContainer = document.querySelector('.sticky-progress-bar-container')
    if (!progressContainer) {
      console.warn('Progress container not found in setupProgressBar!')
      return
    }

    // console.log('setupProgressBar called:', {
    //   hasDiscount: !!this.bundleData?.discount,
    //   discountType: this.bundleData?.discount?.discountType,
    //   hasTiers: !!this.bundleData?.discount?.tiers?.length,
    //   bundleData: this.bundleData
    // })

    // Check discount type and setup accordingly
    if (this.bundleData?.discount) {
      const discountConfig = this.bundleData.discount
      
      if (discountConfig.discountType === 'allProducts') {
        // console.log('Setting up allProducts progress bar')
        this.setupAllProductsProgressBar(progressContainer, discountConfig)
      } else if (discountConfig.discountType === 'tierDiscount' && discountConfig.tiers) {
        // console.log('Setting up tierDiscount progress bar')
        this.setupTierDiscountProgressBar(progressContainer, discountConfig)
      } else {
        // console.log('Hiding progress bar - invalid discount config')
        this.hideProgressBar(progressContainer)
      }
    } else {
      // console.log('Hiding progress bar - no discount config')
      this.hideProgressBar(progressContainer)
    }
  }

  private setupAllProductsProgressBar(container: Element, discountConfig: BundleDiscount): void {
    const allCheckboxes = this.form.querySelectorAll('input[type="checkbox"]:not([disabled])') as NodeListOf<HTMLInputElement>
    const totalItems = allCheckboxes.length
    const discountPercent = parseFloat(discountConfig.discount || '0')
    
    // Hide the progress bar and show friendly message
    const progressBarWrapper = container.querySelector('.progress-bar-container-wrapper')
    if (progressBarWrapper) {
      progressBarWrapper.innerHTML = `
        <div class="progress-legend">
          Select all ${totalItems} products to get ${discountPercent}% off your bundle!
        </div>
      `
    }
    
    this.updateAllProductsDisplay(container)
  }

  private setupTierDiscountProgressBar(container: Element, discountConfig: BundleDiscount): void {
    const allCheckboxes = this.form.querySelectorAll('input[type="checkbox"]:not([disabled])') as NodeListOf<HTMLInputElement>
    const totalItems = allCheckboxes.length
    const tiers = discountConfig.tiers || []
    
    // console.log('Setup tier discount progress bar:', {
    //   totalItems,
    //   tiers,
    //   containerFound: !!container
    // })
    
    // Sort tiers by minQuantity
    const sortedTiers = [...tiers].sort((a, b) => a.minQuantity - b.minQuantity)
    
    const progressBarWrapper = container.querySelector('.progress-bar-container-wrapper')
    if (progressBarWrapper) {
      const htmlContent = `
        <div class="progress-legend">
          ${this.generateDynamicProgressText(0, totalItems, sortedTiers)}
        </div>
        <div class="container-discount-progress-bar">
          <div class="progress-bar-container">
            <div class="progress-bar" style="width: 0%;"></div>
          </div>
          ${this.generateTierMilestones(sortedTiers, totalItems)}
          <div class="current-value-display">
            <span>0 / ${totalItems}</span> items selected
          </div>
        </div>
      `
      
      // console.log('Generated HTML for progress bar:', htmlContent)
      progressBarWrapper.innerHTML = htmlContent
    }
    
    this.progressBar = container.querySelector('.progress-bar')
    this.currentValueDisplay = container.querySelector('.current-value-display span')
    
    // console.log('Progress bar elements after setup:', {
    //   progressBar: !!this.progressBar,
    //   currentValueDisplay: !!this.currentValueDisplay
    // })
    
    this.updateTierProgressBar(container)
  }

  private generateTierMilestones(tiers: DiscountTier[], totalItems: number): string {
    // Filter tiers to only include those achievable with available products
    const achievableTiers = tiers.filter(tier => tier.minQuantity <= totalItems)
    
    return achievableTiers.map(tier => {
      const leftPercent = (tier.minQuantity / totalItems) * 100
      return `
        <div class="milestone" style="left: ${leftPercent}%; transform: translateX(-50%);">
          <div class="milestone-discount">
            ${tier.discount}%
          </div>
          <div class="milestone-marker"></div>
          <div class="milestone-quantity">
            ${tier.minQuantity}+ items
          </div>
        </div>
      `
    }).join('')
  }

  private generateDynamicProgressText(selectedCount: number, totalItems: number, tiers: DiscountTier[]): string {
    const sortedTiers = [...tiers].sort((a, b) => a.minQuantity - b.minQuantity)
    
    // Filter tiers to only include those achievable with available products
    // Example: If tiers are [2=9%, 22=22%] but only 10 products available,
    // only the 9% tier is achievable, so it becomes the "maximum"
    const achievableTiers = sortedTiers.filter(tier => tier.minQuantity <= totalItems)
    const maxAchievableTier = achievableTiers[achievableTiers.length - 1]
    
    // If no tiers are achievable, show default message
    if (!maxAchievableTier) {
      return `Select more items to unlock bigger discounts!`
    }
    
    // If all items are selected
    if (selectedCount === totalItems) {
      return `Congratulations! You have the maximum discount of ${maxAchievableTier.discount}%!`
    }
    
    // Find current tier and next achievable tier
    let currentTier = null
    let nextTier = null
    
    for (let i = 0; i < achievableTiers.length; i++) {
      if (selectedCount >= achievableTiers[i].minQuantity) {
        currentTier = achievableTiers[i]
      } else {
        nextTier = achievableTiers[i]
        break
      }
    }
    
    // If already at max achievable tier but not all selected
    if (currentTier === maxAchievableTier && selectedCount < totalItems) {
      const remainingItems = totalItems - selectedCount
      return `You're at the maximum discount! Complete your bundle with ${remainingItems} more item${remainingItems > 1 ? 's' : ''} and get ${maxAchievableTier.discount}% off everything!`
    }
    
    // If there's a next achievable tier to reach
    if (nextTier) {
      const itemsNeeded = nextTier.minQuantity - selectedCount
      return `Select ${itemsNeeded} more item${itemsNeeded > 1 ? 's' : ''} to unlock ${nextTier.discount}% discount!`
    }
    
    // Default fallback
    return `Select more items to unlock bigger discounts!`
  }

  private hideProgressBar(container: Element): void {
    container.classList.add('hidden')
  }

  private updateProgressDisplay(): void {
    const progressContainer = document.querySelector('.sticky-progress-bar-container')
    if (!progressContainer) {
      console.warn('Progress container not found!')
      return
    }

    // console.log('updateProgressDisplay called:', {
    //   hasDiscount: !!this.bundleData?.discount,
    //   discountType: this.bundleData?.discount?.discountType,
    //   hasTiers: !!this.bundleData?.discount?.tiers?.length
    // })

    if (this.bundleData?.discount) {
      const discountConfig = this.bundleData.discount
      
      if (discountConfig.discountType === 'allProducts') {
        // console.log('Updating allProducts display')
        this.updateAllProductsDisplay(progressContainer)
      } else if (discountConfig.discountType === 'tierDiscount' && discountConfig.tiers) {
        // console.log('Updating tierDiscount display')
        this.updateTierProgressBar(progressContainer)
      }
    }
  }

  private updateTierProgressBar(container: Element): void {
    // Re-find elements each time to ensure they exist after HTML regeneration
    const progressBar = container.querySelector('.progress-bar') as HTMLElement
    const currentValueDisplay = container.querySelector('.current-value-display span') as HTMLElement
    const progressLegend = container.querySelector('.progress-legend') as HTMLElement
    
    // console.log('Progress bar elements found:', {
    //   progressBar: !!progressBar,
    //   currentValueDisplay: !!currentValueDisplay,
    //   progressLegend: !!progressLegend,
    //   container: container.className
    // })
    
    if (!progressBar || !currentValueDisplay || !progressLegend) {
      console.warn('Progress bar elements not found!')
      return
    }

    const allCheckboxes = this.form.querySelectorAll('input[type="checkbox"]:not([disabled])')
    const checkedBoxes = this.form.querySelectorAll('input[type="checkbox"]:not([disabled]):checked')
    
    const currentValue = checkedBoxes.length
    const maxValue = allCheckboxes.length
    const progressPercent = (currentValue / maxValue) * 100
    
    // console.log('Progress bar update:', {
    //   currentValue,
    //   maxValue,
    //   progressPercent,
    //   progressBarWidth: progressBar.style.width
    // })
    
    // Update progress bar
    progressBar.style.width = `${progressPercent}%`
    currentValueDisplay.textContent = `${currentValue} / ${maxValue}`
    
    // Update dynamic text
    if (this.bundleData?.discount?.tiers) {
      const newText = this.generateDynamicProgressText(currentValue, maxValue, this.bundleData.discount.tiers)
      const currentText = progressLegend.textContent
      if (currentText !== newText) {
        this.updateProgressLegendWithAnimation(progressLegend, newText)
      }
      
      // Add success styling if all items are selected
      if (currentValue === maxValue) {
        progressLegend.classList.add('success-message')
      } else {
        progressLegend.classList.remove('success-message')
      }
    }
  }

  private updateAllProductsDisplay(container: Element): void {
    const allCheckboxes = this.form.querySelectorAll('input[type="checkbox"]:not([disabled])')
    const checkedBoxes = this.form.querySelectorAll('input[type="checkbox"]:not([disabled]):checked')
    
    const currentValue = checkedBoxes.length
    const maxValue = allCheckboxes.length
    const isAllSelected = currentValue === maxValue
    
    const progressLegend = container.querySelector('.progress-legend') as HTMLElement
    if (progressLegend && this.bundleData?.discount) {
      const discountPercent = parseFloat(this.bundleData.discount.discount || '0')
      let newText: string
      
      if (isAllSelected) {
        newText = `Great! You've selected all ${maxValue} products and earned ${discountPercent}% off!`
      } else {
        newText = `Select all ${maxValue} products to get ${discountPercent}% off your bundle! (${currentValue}/${maxValue} selected)`
      }
      
      const currentText = progressLegend.textContent
      
      // Check if text changed and apply animation
      if (currentText !== newText) {
        this.updateProgressLegendWithAnimation(progressLegend, newText)
      }
      
      // Add or remove success styling
      if (isAllSelected) {
        progressLegend.classList.add('success-message')
      } else {
        progressLegend.classList.remove('success-message')
      }
    }
  }

  private hideStickyCTA(): void {
    const stickyCTA = document.querySelector('.product-sticky-cta') as HTMLElement
    if (stickyCTA) {
      stickyCTA.style.display = 'none'
    }
  }

  private syncTooltipInputs(tooltip: Element, trigger: Element): void {
    const uniqueId = trigger.id.replace('label-', '')
    
    // Find the associated checkbox to get current values
    let checkbox = this.form.querySelector(`input[type="checkbox"][data-unique-id="${uniqueId}"]`) as HTMLInputElement
    
    if (!checkbox) {
      // Fallback: find by tooltip container
      const tooltipContainer = tooltip.closest('.recipe-content__ingredient-item')
      checkbox = tooltipContainer?.querySelector('input[type="checkbox"]') as HTMLInputElement
      if (!checkbox) return
    }

    // Sync quantity input - prioritize label quantity
    const quantityInput = tooltip.querySelector(`#quantity-${uniqueId}`) as HTMLInputElement
    if (quantityInput) {
      const labelQuantitySpan = trigger.querySelector('.label-quantity')
      const labelQuantity = labelQuantitySpan ? labelQuantitySpan.textContent : '1'
      const currentQuantity = checkbox.getAttribute('data-quantity') || '1'
      quantityInput.value = labelQuantity || currentQuantity
    }

    // Sync variant select
    const variantSelect = tooltip.querySelector(`#variant-${uniqueId}`) as HTMLSelectElement
    if (variantSelect) {
      variantSelect.value = checkbox.value
    }
  }

  private updateRecipesFilter(): void {
    // Get all checked checkboxes to get selected product IDs
    const checkedCheckboxes = this.form.querySelectorAll('input[type="checkbox"]:checked') as NodeListOf<HTMLInputElement>
    const selectedProductIds = new Set<string>()
    
    checkedCheckboxes.forEach(checkbox => {
      const productId = checkbox.getAttribute('data-product-id')
      if (productId) {
        selectedProductIds.add(productId)
      }
    })
    
    // console.log('Selected product IDs:', Array.from(selectedProductIds))
    
    // Get recipes from bundle data
    const recipes = this.bundleData?.recipes || []
    if (recipes.length === 0) {
      const recipesCounterContainer = document.querySelector('.recipes-counter-container')
      if (recipesCounterContainer) {
        recipesCounterContainer.classList.add('hidden')
      }
      const recipesCounter = document.querySelector('#fixed-recipes-counter')
      if (recipesCounter) {
        recipesCounter.classList.add('hidden')
      }
      return
    }
    
    // Filter recipes where all ingredients are selected
    const availableRecipes = recipes.filter(recipe => {
      if (!recipe.ingredients || !Array.isArray(recipe.ingredients)) {
        return false
      }
      
      // Check if all ingredients are selected
      return recipe.ingredients.every((ingredientId: string) => selectedProductIds.has(ingredientId))
    })
    
    // console.log('Available recipes:', availableRecipes.length, 'out of', recipes.length)
    
    // Update the recipes display
    this.updateRecipesDisplay(availableRecipes)
    
    // Update recipe counters
    this.updateRecipeCounters(availableRecipes.length)
  }

  private updateRecipesDisplay(availableRecipes: any[]): void {
    const recipesContainer = document.querySelector('.bundle-related-recipes')
    if (!recipesContainer) {
      // console.log('Recipes container not found')
      return
    }
    
    const recipesGrid = recipesContainer.querySelector('.bundle-related-recipes__grid')
    if (!recipesGrid) {
      // console.log('Recipes grid not found')
      return
    }
    
    if (availableRecipes.length === 0) {
      // Hide recipes section if no recipes are available
      recipesContainer.classList.add('hidden')
      return
    }
    
    // Show recipes section
    recipesContainer.classList.remove('hidden')
    
    // Generate HTML for available recipes
    const recipesHTML = availableRecipes.map(recipe => `
      <div class="recipe-card">
        <a
          href="/blogs/recipes/${recipe.handle}"
          target="_blank"
          class="recipe-card__link"
        >
          <div class="recipe-card__image-container">
            ${recipe.featured_image ? `
              <img
                src="${recipe.featured_image}"
                alt="${recipe.title}"
                class="recipe-card__image"
                loading="lazy"
                width="280"
                height="200"
              >
            ` : ''}
          </div>

          <div class="recipe-card__content">
            <h3 class="recipe-card__title">
              ${recipe.title}
            </h3>
            <span class="recipe-card__cta">View Recipe</span>
          </div>
        </a>
      </div>
    `).join('')
    
    // Update the grid content
    recipesGrid.innerHTML = recipesHTML
    
    // console.log('Updated recipes display with', availableRecipes.length, 'recipes')
  }

  private updateRecipeCounters(count: number): void {
    // Update sticky bar counter
    const stickyCounter = document.getElementById('recipes-count')
    if (stickyCounter) {
      const stickyCounterContainer = stickyCounter.parentElement
      this.updateCounterWithAnimation(stickyCounter, stickyCounterContainer, count)
    }
    
    // Update fixed counter
    const fixedCounter = document.getElementById('fixed-recipes-count')
    if (fixedCounter) {
      const fixedCounterContainer = fixedCounter.parentElement
      this.updateCounterWithAnimation(fixedCounter, fixedCounterContainer, count)
    }
  }

  private updateCounterWithAnimation(counterElement: HTMLElement, containerElement: HTMLElement | null, newCount: number): void {
    const currentCount = parseInt(counterElement.textContent || '0')
    
    if (currentCount !== newCount) {
      // Add animation class
      if (containerElement) {
        containerElement.classList.add('updated')
      }
      
      // Update the count
      counterElement.textContent = newCount.toString()
      
      // Remove animation class after animation completes
      setTimeout(() => {
        if (containerElement) {
          containerElement.classList.remove('updated')
        }
      }, 400)
    }
  }

  private updateProgressLegendWithAnimation(progressLegend: HTMLElement, newText: string): void {
    // Add animation class
    progressLegend.classList.add('updated')
    
    // Update the text
    progressLegend.textContent = newText
    
    // Remove animation class after animation completes
    setTimeout(() => {
      progressLegend.classList.remove('updated')
    }, 400)
  }
}
